/**
 * Core runtime engine for the sandbox - handles compilation and execution.
 * This module is framework-agnostic and can be used by React or other UI.
 *
 * Compilation and module loading are delegated to @gotti/sandbox-core
 * so the same code runs identically in the browser and on the backend.
 */
import type { MockEnvironment } from '@gotti/test-utils';
import { inputTokenOf, isPlaygroundPair, mountPlayground, playgroundEntries, type PlaygroundConfig, type PlaygroundMount } from '@gotti/playground/browser';
import {
  applySandboxLatency,
  sandboxClientId,
  type SandboxEnvironment,
} from '@gotti/sandbox-test';
import * as GottiClient from '@gotti/client';
import * as GottiServer from '@gotti/server';
import * as GottiCore from '@gotti/core';
import type { AssetSource } from '@gotti/core';
import { createBrowserAssetSource, checkedAssetPreloadPaths } from '../assets/browserAssetSource';
import {
  loadPlaygroundProject,
  loadSystemsAndExports as coreLoadSystems,
  migrateV1Project,
  type ProjectTree,
  type SandboxSystemsInput as CoreSystemsInput,
} from '@gotti/sandbox-core';
import { sandboxExternals } from './sandboxExternals';
import { definitionClientLayers } from './definitionClient';
import type { SandboxSystemsInput, ClientRecord } from './types';
import { DEFAULT_CLIENT_SETTINGS } from './types';

const { isClientSystem } = GottiClient;
const { isServerSystem } = GottiServer;

/**
 * The asset seam a boot feeds into the test-utils `initializer`: the byte
 * `source` the per-boot `ServerAssetManager` reads through, and the full
 * namespaced `preload` paths warmed before any system starts (so a sync
 * `$assets.client.getImage(...)` is ready in `onUpdate`).
 */
export interface BootAssetSeam {
  source: AssetSource;
  preload: string[];
}

/**
 * Optional per-boot asset-seam override. The LIVE preview boot uses the session
 * store (`createBrowserAssetSource()` + the checked-asset list) computed at boot
 * time. The in-browser TEST runner needs a different source
 * (`createTestAssetSource(refs)`) but cannot thread it through `bootRealRuntime`
 * (which returns a `BootedGame`, not the environment), so it installs an override
 * here that every boot reads through `getBootAssetSeam` — the per-boot analog of the
 * old shared-singleton feed the runner set up in `executeTests`. The runner
 * clears it in its `finally`.
 */
let bootAssetSeamOverride: BootAssetSeam | null = null;
export function setBootAssetSeamOverride(seam: BootAssetSeam | null): void {
  bootAssetSeamOverride = seam;
}

/** The asset seam a boot uses: the test runner's override when one is installed, else the session store and its checked-asset list. */
export function getBootAssetSeam(): BootAssetSeam {
  return bootAssetSeamOverride ?? { source: createBrowserAssetSource(), preload: checkedAssetPreloadPaths() };
}

/**
 * The `ServerAssetManager` the MOST RECENT boot created (its game systems'
 * `$assets` read/write it). The in-browser test runner reads this right after
 * `bootRealRuntime` returns to point its CanvasProbe asset host at the SAME
 * per-boot manager — `bootRealRuntime` returns a `BootedGame` that doesn't carry
 * it, and we deliberately don't thread it through. Boots are serial, so "last
 * boot" is unambiguous for the reader.
 */
let lastBootAssetManager: MockEnvironment['assetManager'] | null = null;
export function getLastBootAssetManager(): MockEnvironment['assetManager'] | null {
  return lastBootAssetManager;
}
/** Records the manager a boot built — every boot path calls it once its environment exists. */
export function setLastBootAssetManager(manager: MockEnvironment['assetManager'] | null): void {
  lastBootAssetManager = manager;
}

export interface RuntimeState {
  environment: MockEnvironment | null;
  /** The playground mount the preview runs on: disposing it ends every loop and the page-wide state. */
  mount: PlaygroundMount;
  clients: ClientRecord[];
  nextClientIdCounter: number;
  moduleCache: Map<string, any>;
  latencyByClientId: Map<string, number>;
  /** Each client's input token (`inputTokenOf`), by client id — what focusing that client means. */
  clientTokens: Map<string, number>;
}

export interface RuntimeCallbacks {
  /** A surface for client `index` (its order among the boot's clients) to draw on. */
  onClientCreated: (client: ClientRecord, isFirst: boolean, index: number) => { canvas: HTMLCanvasElement; container: HTMLElement };
}

// Registry of available external modules for the sandbox
const externalModules = new Map<string, any>([
  ['@gotti/client', GottiClient],
  ['@gotti/server', GottiServer],
  ['@gotti/core', GottiCore],
]);
/**
 * Load all systems and their exports from virtual files.
 * Delegates to @gotti/sandbox-core with real @gotti/* runtime modules.
 */
export function loadSystemsAndExports(
  files: Map<string, string>,
  /** Shared module context so the runtime can reuse the test runner's already-
   *  compiled `@/shared/*` instances (identical component/entity classes). When
   *  omitted (live preview / standalone boot), uses the runtime's own external
   *  modules + a fresh cache. See `coreLoadSystems`' `moduleCache` doc. */
  shared?: { externalModules?: Map<string, any>; moduleCache?: Map<string, any> },
) {
  return coreLoadSystems(
    files,
    shared?.externalModules ?? externalModules,
    (cls: any) => { try { return isClientSystem(cls); } catch { return false; } },
    (cls: any) => { try { return isServerSystem(cls); } catch { return false; } },
    console,
    shared?.moduleCache,
  );
}

/** Which of a project's playgrounds a boot mounts, and the name the mount goes by. */
export interface ProjectPlaygroundTarget {
  /** The tree path of the definition file — the config, for an inline entry. */
  file: string;
  /** An inline entry of the config's `playgrounds`: its key (or `name`). */
  member?: string;
  /** For a pair: which half. Default `'online'`. */
  variant?: 'online' | 'offline';
  name: string;
}

/** The config's inline entry `member`, as `gotti playground` finds it (`playgroundEntries`); refused when there is none. */
const inlineEntry = (config: Record<string, unknown>, member: string): unknown => {
  for (const declared of playgroundEntries(config as unknown as PlaygroundConfig)) {
    if ('name' in declared && declared.name === member) return declared.entry;
  }
  throw new Error(`playground.config.ts has no playground "${member}" in its \`playgrounds\`.`);
};

/** The live preview's playground: the project's own `sandbox.playground.ts`. */
const PREVIEW_PLAYGROUND: ProjectPlaygroundTarget = { file: 'sandbox.playground.ts', name: 'sandbox-preview' };

/** Whether the preview was handed a project tree or a v1 project (the e2e specs and the parity harness pass one). */
const isProjectTree = (input: SandboxSystemsInput | ProjectTree): input is ProjectTree => 'files' in input && 'topology' in input;

/**
 * Initialize and run the sandbox environment: a project tree as it is, a v1 project migrated first — either way the
 * project's own playground definition boots (`bootRuntimeFromProject`).
 */
export async function initializeRuntime(
  input: SandboxSystemsInput | ProjectTree,
  callbacks: RuntimeCallbacks,
  /** Aborting it gives the boot up (see `bootRuntimeFromProject`). */
  signal?: AbortSignal,
): Promise<RuntimeState> {
  const tree = isProjectTree(input) ? input : migrateV1Project(input as CoreSystemsInput, { name: 'sandbox' });
  return bootRuntimeFromProject(tree, callbacks, DEFAULT_CLIENT_SETTINGS.simulatedPing, PREVIEW_PLAYGROUND, signal);
}

/**
 * Boots the live preview from the project itself: its playground config and definition (`sandbox.playground.ts`, or
 * `playground`'s file) loaded from the tree by the in-browser project loader — so its Game, Area and registry files are
 * what run — and mounted through the playground on the real clock. The site's own pieces stay out of the person's
 * files: each client draws on the surface `callbacks.onClientCreated` hands out, given as the config's
 * `clientConfig.domSetup` (under the definition's own `clientConfig`), and an object definition gets the session's
 * asset seam (`getBootAssetSeam`) in its environment — a function or pair definition is mounted as it is. Per-client
 * latency is applied after the boot, as before. A file that fails to load rejects with the loader's error, naming it,
 * before anything is mounted.
 */
export async function bootRuntimeFromProject(
  tree: ProjectTree,
  callbacks: RuntimeCallbacks,
  /** Simulated one-way client↔server latency (ms): each client's `simulatedLatency`. Defaults to the preview's
   *  `simulatedPing`; the project's config sets the transport's own (`mockConfig`). `null` sets no per-client
   *  latency, so the declared one governs — a test mount's, as under `gotti playground`. */
  latencyMs: number | null = DEFAULT_CLIENT_SETTINGS.simulatedPing,
  playground: ProjectPlaygroundTarget = PREVIEW_PLAYGROUND,
  /**
   * Aborting it while the playground boots gives the boot up: it rejects with the playground's
   * `PlaygroundMountCancelledError`, and the page is free for the next mount at once (`mountPlayground`'s `signal`).
   */
  signal?: AbortSignal,
): Promise<RuntimeState> {
  const project = await loadPlaygroundProject(tree.files, { externalModules: sandboxExternals(), console, definitionPath: playground.file });

  // A client's surface is made when the playground creates it — the definition says how many there are.
  const surfaces: { canvas: HTMLCanvasElement; container: HTMLElement }[] = [];
  const surfaceFor = (index: number) =>
    (surfaces[index] ??= callbacks.onClientCreated({ id: sandboxClientId(index), settings: { ...DEFAULT_CLIENT_SETTINGS } }, index === 0, index));
  const config = project.config as { clientConfig?: Record<string, unknown> };
  const exported = playground.member === undefined
    ? (project.definition.module as { default?: unknown }).default ?? project.definition.module
    : inlineEntry(project.config, playground.member);
  const layeredDefinition = exported && typeof exported === 'object' && !isPlaygroundPair(exported)
    ? { ...exported, environment: { ...(exported as { environment?: object }).environment, assets: getBootAssetSeam() } }
    : exported;

  // The preview draws only on the surfaces it hands out, so the mount's own target is never attached.
  const mount = await mountPlayground({
    name: playground.name,
    target: document.createElement('div'),
    definition: layeredDefinition as never,
    config: { ...config, clientConfig: { ...config.clientConfig, domSetup: (index: number) => surfaceFor(index) } } as never,
    ...(playground.variant ? { variant: playground.variant } : {}),
    ...(signal ? { signal } : {}),
  });
  const environment = mount.environment;
  if (latencyMs !== null) applySandboxLatency(environment as unknown as SandboxEnvironment, latencyMs);

  // Expose this boot's manager so the in-browser test runner can point its
  // CanvasProbe asset host at the SAME per-boot manager the game's `$assets`
  // reads (see getLastBootAssetManager).
  setLastBootAssetManager(environment?.assetManager ?? null);

  // One record per surface handed out, in that order: the client drawing on it, under the id it authenticated as —
  // the definition's `clientAuth` names it, not the preview. Paired by canvas, never by position, so a client the
  // definition makes some other way (an `initClients` one, drawing on no surface of the preview's) takes no one's place.
  const created = [...new Set(Object.values(environment?.clients ?? {}))];
  const clients: ClientRecord[] = [];
  const clientTokens = new Map<string, number>();
  const latencyByClientId = new Map<string, number>();
  surfaces.forEach((surface, index) => {
    const instance = created.find((client) => (client as { envOptions?: { canvas?: unknown } }).envOptions?.canvas === surface.canvas);
    const id = instance ? String(instance.id) : sandboxClientId(index);
    clients.push({ id, settings: { ...DEFAULT_CLIENT_SETTINGS }, instance });
    if (instance) clientTokens.set(id, inputTokenOf(instance as never));
    if (latencyMs !== null) latencyByClientId.set(id, latencyMs);
  });

  return {
    environment,
    mount,
    clients,
    // The definition numbered the clients it booted 0…n-1 (`clientAuth({ index })`); an added one continues from there.
    nextClientIdCounter: surfaces.length,
    moduleCache: new Map(),
    latencyByClientId,
    clientTokens,
  };
}

/**
 * Add a new client to an existing environment.
 */
export async function addClientToRuntime(
  state: RuntimeState,
  callbacks: RuntimeCallbacks,
  /** Per-client simulated latency (ms) for the joining client — see
   *  `bootRuntimeFromProject`. Defaults to realistic `simulatedPing` (live
   *  preview "+ Add Client"); the test booter passes the test's `latency`. */
  latencyMs: number = DEFAULT_CLIENT_SETTINGS.simulatedPing,
): Promise<{ client: ClientRecord; nextCounter: number }> {
  if (!state.environment) {
    throw new Error('Cannot add client - environment not initialized');
  }

  // The client is the definition's next one (`definitionClientLayers`): the numbering continues, so an added client
  // never reuses a removed one's index — nor, with the scaffold's `player-${index + 1}`, its id.
  const index = state.nextClientIdCounter;
  const nextCounter = index + 1;
  let surface: ReturnType<RuntimeCallbacks['onClientCreated']> | undefined;
  const layers = definitionClientLayers(state.mount.selection, index, () =>
    (surface ??= callbacks.onClientCreated({ id: sandboxClientId(index), settings: { ...DEFAULT_CLIENT_SETTINGS } }, false, index)));

  // The playground scopes the new client's key listeners under its own input token as the client is created.
  const [clientInstance] = await state.environment.createClients(1, layers.definition, layers.config);
  clientInstance.simulatedLatency = latencyMs;
  const clientId = String(clientInstance.id);
  state.clientTokens.set(clientId, inputTokenOf(clientInstance));
  state.latencyByClientId.set(clientId, latencyMs);

  return {
    client: { id: clientId, settings: { ...DEFAULT_CLIENT_SETTINGS }, instance: clientInstance },
    nextCounter,
  };
}

/**
 * Remove a client from the environment.
 */
export async function removeClientFromRuntime(
  state: RuntimeState,
  clientId: string,
): Promise<void> {
  const client = state.clients.find(c => c.id === clientId);
  if (!client) return;

  state.latencyByClientId.delete(clientId);
  state.clientTokens.delete(clientId);

  if (client.instance && state.environment) {
    try {
      if (state.environment.removeClient) {
        await state.environment.removeClient(client.instance);
      } else {
        const instance = client.instance;
        if (state.environment.areas && instance.process?.joinedAreasById) {
          const areaIds = Object.keys(instance.process.joinedAreasById);
          areaIds.forEach(areaId => {
            const area = state.environment!.areas[areaId];
            if (area && typeof area.handleClientLeave === 'function') {
              area.handleClientLeave(instance.id, GottiCore.LEAVE_AREA_REASONS.DISCONNECTED, { reason: 'Sandbox Removed' });
            }
          });
        }
        if (instance.process) instance.process.stopLoop();
        if (instance.connector) instance.connector.disconnect();
      }
    } catch (e) {
      console.warn('Error disconnecting client', e);
    }
  }
}

/**
 * Stop the runtime: dispose its mount, which stops every client AND area loop, tears down the active environment
 * and resets the page-wide queues, focus and input state — so the next mount (a re-run, or a test run's boot) starts
 * clean. What a game's `onStop` throws is logged, not raised: the preview is gone either way.
 */
export async function stopRuntime(state: RuntimeState): Promise<void> {
  try {
    await state.mount.dispose();
  } catch (e) {
    console.warn('[Sandbox] the game threw while it stopped', e);
  }
}
